/**
 * Personal performance dashboards: Business, Portfolio, and user-created KPI tabs.
 * Each user picks KPIs per tab; that list is saved for that user.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ColorKey } from '@/components/business-performance/performance/ColorKey';
import { ColumnsControls } from '@/components/business-performance/performance/ColumnsControls';
import { DockedRail } from '@/components/business-performance/performance/DockedRail';
import { KpiPickerPanel } from '@/components/business-performance/performance/KpiPickerPanel';
import { KpiTileGrid } from '@/components/business-performance/performance/KpiTileGrid';
import { PerformanceTabStrip } from '@/components/business-performance/performance/PerformanceTabStrip';
import { PortfolioCharts } from '@/components/business-performance/performance/PortfolioCharts';
import { CompositeGrid } from '@/components/business-performance/performance/CompositeGrid';
import { PortfolioPeriodControls } from '@/components/business-performance/performance/PortfolioPeriodControls';
import { sortTiles } from '@/components/business-performance/performance/sortTiles';
import {
  DEFAULT_KPI_COLUMNS,
  DEFAULT_TILE_SORT,
  defaultComparePrefs,
  defaultPortfolioView,
  emptyCompareOptions,
  type ComparePrefs,
  type CompositeDef,
  type CustomTab,
  type KpiRowColumns,
  type TileSort,
  type PortfolioView,
  type TabId,
  type TabPayload
} from '@/components/business-performance/performance/types';

function newTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ct-${crypto.randomUUID()}`;
  }
  return `ct-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const PerformanceTabs: React.FC = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState<TabId>('business');
  const [data, setData] = useState<TabPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingHide, setSavingHide] = useState(false);

  const loadTab = useCallback(
    async (nextTab: TabId, options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      setError('');
      try {
        const response = await api.get('/business-performance/performance', {
          params: { tab: nextTab }
        });
        const payload = (response.data?.data || null) as TabPayload | null;
        setData(payload);
        if (payload?.tab) setTab(payload.tab);
      } catch {
        setError(t('kpi.perfLoadFailed') || 'Could not load performance dashboard.');
      } finally {
        if (!options?.silent) setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadTab(tab);
  }, [tab, loadTab]);

  const persistTabs = async (tabs: CustomTab[], nextTab?: TabId) => {
    setData((current) => (current ? { ...current, customTabs: tabs } : current));
    try {
      const response = await api.put('/business-performance/performance/tabs', { tabs });
      const saved = (response.data?.data?.tabs || tabs) as CustomTab[];
      setData((current) => (current ? { ...current, customTabs: saved } : current));
      if (nextTab) setTab(nextTab);
    } catch {
      setError(t('kpi.perfTabsFailed') || 'Could not update tabs.');
      await loadTab(tab, { silent: true });
    }
  };

  const persistCompare = async (compare: ComparePrefs) => {
    setSavingHide(true);
    setData((current) => (current ? { ...current, compare } : current));
    try {
      await api.put('/business-performance/performance/compare', compare);
      await loadTab(tab, { silent: true });
    } catch {
      setError(t('kpi.perfCompareFailed') || 'Could not save comparison.');
    } finally {
      setSavingHide(false);
    }
  };

  const persistPortfolioView = async (portfolioView: PortfolioView) => {
    setSavingHide(true);
    setData((current) => (current ? { ...current, portfolioView } : current));
    try {
      await api.put('/business-performance/performance/portfolio-view', portfolioView);
      await loadTab(tab, { silent: true });
    } catch {
      setError(t('kpi.perfPortfolioViewFailed') || 'Could not save portfolio period.');
    } finally {
      setSavingHide(false);
    }
  };

  const previewColumns = (columns: KpiRowColumns) => {
    setData((current) => (current ? { ...current, columns } : current));
  };

  const persistSort = async (sort: TileSort) => {
    setData((current) => (current ? { ...current, sort } : current));
    try {
      await api.put('/business-performance/performance/sort', { sort });
    } catch {
      setError(t('kpi.perfSortFailed') || 'Could not save sort.');
      await loadTab(tab, { silent: true });
    }
  };

  const persistColumns = async (columns: KpiRowColumns) => {
    setData((current) => (current ? { ...current, columns } : current));
    try {
      await api.put('/business-performance/performance/columns', { columns });
    } catch {
      setError(t('kpi.perfColumnsFailed') || 'Could not save KPIs per row.');
      await loadTab(tab, { silent: true });
    }
  };

  const persistSelected = async (kpiIds: string[]) => {
    if (tab === 'portfolio') return;
    setSavingHide(true);
    setData((current) => (current ? { ...current, selectedIds: kpiIds } : current));
    try {
      await api.put('/business-performance/performance/visibility', { tab, kpiIds });
      await loadTab(tab, { silent: true });
    } catch {
      setError(t('kpi.perfSelectFailed') || 'Could not save KPI selection.');
    } finally {
      setSavingHide(false);
    }
  };

  const persistComposites = async (composites: CompositeDef[]) => {
    if (tab === 'portfolio') return;
    setSavingHide(true);
    try {
      await api.put('/business-performance/performance/composites', { tab, composites });
      await loadTab(tab, { silent: true });
    } catch {
      setError(t('kpi.compositeFailed') || 'Could not save composite.');
    } finally {
      setSavingHide(false);
    }
  };

  const customTabs = data?.customTabs || [];
  const catalog = data?.catalog || [];
  const isCustom = tab !== 'business' && tab !== 'portfolio';
  const showPicker = tab === 'business' || isCustom;
  const selectedCount = data?.selectedIds?.length || 0;
  const sortedTiles = useMemo(
    () => sortTiles(data?.tiles || [], data?.sort || DEFAULT_TILE_SORT, data?.selectedIds || []),
    [data?.tiles, data?.sort, data?.selectedIds]
  );

  // Keep the open custom tab's kpiIds in sync so rename/add/delete do not wipe a pending selection.
  const tabsForSave = (): CustomTab[] =>
    customTabs.map((item) =>
      item.id === tab ? { ...item, kpiIds: data?.selectedIds || item.kpiIds } : item
    );

  const addTab = () => {
    const next: CustomTab = {
      id: newTabId(),
      name: `${t('kpi.perfNewTab') || 'Tab'} ${customTabs.length + 1}`,
      kpiIds: []
    };
    void persistTabs([...tabsForSave(), next], next.id);
  };

  const renameTab = (id: string, name: string) => {
    void persistTabs(tabsForSave().map((item) => (item.id === id ? { ...item, name } : item)));
  };

  const deleteTab = (id: string) => {
    const remaining = tabsForSave().filter((item) => item.id !== id);
    void persistTabs(remaining, tab === id ? 'business' : undefined);
  };

  return (
    <section className="mb-3 border border-[var(--color-border)] rounded bg-[var(--color-surface)] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <PerformanceTabStrip
          tab={tab}
          customTabs={customTabs}
          onSelect={setTab}
          onAdd={addTab}
          onRename={renameTab}
          onDelete={deleteTab}
          canAdd={!loading && !!data}
          t={t}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/kpis/loads"
            className="text-xs text-[var(--color-primary-text)] hover:underline"
          >
            {t('kpi.loadTitle')}
          </Link>
          {data?.scope && (
            <p className="text-xs text-[var(--color-secondary-text)]">
              {[data.scope.businessName, data.scope.functionalName, data.scope.geographicName]
                .filter(Boolean)
                .join(' · ') ||
                t('kpi.perfPersonalScope') ||
                'Your hierarchy'}
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {loading && (
        <p className="text-sm text-[var(--color-secondary-text)]">
          {t('common.loading') || 'Loading...'}
        </p>
      )}

      {!loading && data?.needsHierarchy && (
        <p className="text-sm text-[var(--color-secondary-text)]">
          {data.message || t('kpi.perfNeedsHierarchy')}
        </p>
      )}

      {!loading && data && !data.needsHierarchy && (
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="flex-1 min-w-0">
            {tab !== 'portfolio' && (
              <>
                <CompositeGrid
                  cards={(data.composites || []).filter((card) => card.hidden !== true)}
                  columns={data.columns || DEFAULT_KPI_COLUMNS}
                  t={t}
                  onHide={(id) => {
                    void persistComposites(
                      (data.composites || []).map((card) => ({
                        id: card.id,
                        tab,
                        name: card.name,
                        kind: card.kind,
                        memberKpiIds: card.memberKpiIds?.length
                          ? card.memberKpiIds
                          : card.slices.map((slice) => slice.kpiId).filter(Boolean),
                        denominatorKpiId: card.denominatorKpiId ?? null,
                        hidden: card.id === id ? true : card.hidden === true
                      }))
                    );
                  }}
                />
                {data.tiles.length === 0 &&
                (data.composites || []).filter((card) => card.hidden !== true).length === 0 ? (
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {isCustom
                      ? t('kpi.perfCustomEmpty') ||
                        'No KPIs on this tab yet. Use the side panel to choose KPIs.'
                      : selectedCount === 0 && catalog.length > 0
                        ? t('kpi.perfAllHidden') ||
                          'No KPIs selected. Use the side panel to choose KPIs.'
                        : t('kpi.perfNoBusinessKpis') ||
                          'No tenant KPIs yet.'}
                  </p>
                ) : data.tiles.length === 0 ? null : (
                  <KpiTileGrid
                    tiles={sortedTiles}
                    columns={data.columns || DEFAULT_KPI_COLUMNS}
                    t={t}
                    onHide={(kpiId) => {
                      void persistSelected((data.selectedIds || []).filter((id) => id !== kpiId));
                    }}
                  />
                )}
              </>
            )}

            {tab === 'portfolio' && data.metrics && (
              <PortfolioCharts
                metrics={data.metrics}
                columns={data.columns || DEFAULT_KPI_COLUMNS}
                t={t}
              />
            )}
          </div>

          {showPicker && (
            <DockedRail
              label={t('kpi.perfPicker') || 'KPIs'}
              collapseLabel={t('kpi.perfHidePanel') || 'Collapse panel'}
              expandLabel={t('kpi.perfShowPanel') || 'Expand panel'}
            >
              <KpiPickerPanel
                catalog={catalog}
                ids={data.selectedIds || []}
                mode="include"
                saving={savingHide}
                compare={data.compare || defaultComparePrefs()}
                options={data.compareOptions || emptyCompareOptions()}
                columns={data.columns || DEFAULT_KPI_COLUMNS}
                onChange={(ids) => {
                  void persistSelected(ids);
                }}
                onCompareChange={(compare) => {
                  void persistCompare(compare);
                }}
                onColumnsPreview={previewColumns}
                onColumnsChange={(columns) => {
                  void persistColumns(columns);
                }}
                sort={data.sort || DEFAULT_TILE_SORT}
                onSortChange={(next) => {
                  void persistSort(next);
                }}
                tab={tab}
                composites={data.composites || []}
                onCompositesChange={(defs) => {
                  void persistComposites(defs);
                }}
                thresholds={data.thresholds}
                t={t}
              />
            </DockedRail>
          )}

          {tab === 'portfolio' && (
            <DockedRail
              label={t('kpi.perfPicker') || 'KPIs'}
              collapseLabel={t('kpi.perfHidePanel') || 'Collapse panel'}
              expandLabel={t('kpi.perfShowPanel') || 'Expand panel'}
            >
              <PortfolioPeriodControls
                view={data.portfolioView || defaultPortfolioView()}
                saving={savingHide}
                onChange={(view) => {
                  void persistPortfolioView(view);
                }}
                t={t}
              />
              <ColumnsControls
                columns={data.columns || DEFAULT_KPI_COLUMNS}
                saving={savingHide}
                onPreview={previewColumns}
                onCommit={(columns) => {
                  void persistColumns(columns);
                }}
                t={t}
              />
              <ColorKey t={t} thresholds={data.thresholds} />
            </DockedRail>
          )}
        </div>
      )}
    </section>
  );
};
